// Spec ref: docs/superpowers/specs/2026-05-21-backend-extension-ui-wireup.md #3
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ApiError,
  deleteAllMeSessions,
  deleteAvatar,
  deleteMeSession,
  fetchAccessLog,
  fetchMe,
  fetchMeExtended,
  fetchMeSessions,
  patchMe,
  regenerateBackupCodes,
  uploadAvatar,
  type AccessLogEntry,
  type AvatarResponse,
  type BackupCodesResponse,
  type MePatchPayload,
  type MeResponse,
  type MeSessionListResponse,
} from '../lib/api'
import { clearTokens, hasTokens } from '../lib/auth'

export const ME_QUERY_KEY = ['auth', 'me'] as const
export const ME_EXTENDED_QUERY_KEY = ['me', 'extended'] as const
export const ME_SESSIONS_QUERY_KEY = ['me', 'sessions'] as const
export const ACCESS_LOG_QUERY_KEY = ['me', 'access-log'] as const

export function useMe() {
  return useQuery<MeResponse, ApiError>({
    queryKey: ME_QUERY_KEY,
    queryFn: fetchMe,
    enabled: hasTokens(),
    retry: false,
    staleTime: 60_000,
  })
}

export function useMeExtended() {
  return useQuery<MeResponse, ApiError>({
    queryKey: ME_EXTENDED_QUERY_KEY,
    queryFn: fetchMeExtended,
    enabled: hasTokens(),
    retry: false,
    staleTime: 30_000,
  })
}

export function usePatchMe() {
  const qc = useQueryClient()
  return useMutation<MeResponse, ApiError, MePatchPayload>({
    mutationFn: patchMe,
    onSuccess: (data) => {
      qc.setQueryData(ME_EXTENDED_QUERY_KEY, data)
      void qc.invalidateQueries({ queryKey: ME_QUERY_KEY })
    },
  })
}

export function useUploadAvatar() {
  const qc = useQueryClient()
  return useMutation<AvatarResponse, ApiError, File>({
    mutationFn: uploadAvatar,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ME_EXTENDED_QUERY_KEY })
      void qc.invalidateQueries({ queryKey: ME_QUERY_KEY })
    },
  })
}

export function useDeleteAvatar() {
  const qc = useQueryClient()
  return useMutation<void, ApiError, void>({
    mutationFn: deleteAvatar,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ME_EXTENDED_QUERY_KEY })
      void qc.invalidateQueries({ queryKey: ME_QUERY_KEY })
    },
  })
}

export function useMeSessions() {
  return useQuery<MeSessionListResponse, ApiError>({
    queryKey: ME_SESSIONS_QUERY_KEY,
    queryFn: fetchMeSessions,
    enabled: hasTokens(),
    retry: false,
    staleTime: 30_000,
  })
}

export function useDeleteSession() {
  const qc = useQueryClient()
  return useMutation<void, ApiError, string>({
    mutationFn: deleteMeSession,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ME_SESSIONS_QUERY_KEY })
    },
  })
}

export function useDeleteAllSessions() {
  const qc = useQueryClient()
  return useMutation<void, ApiError, void>({
    mutationFn: deleteAllMeSessions,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ME_SESSIONS_QUERY_KEY })
    },
  })
}

export function useAccessLog(limit = 10) {
  return useQuery<AccessLogEntry[], ApiError>({
    queryKey: [...ACCESS_LOG_QUERY_KEY, limit],
    queryFn: () => fetchAccessLog(limit),
    enabled: hasTokens(),
    retry: false,
    staleTime: 30_000,
  })
}

export function useRegenerateBackupCodes() {
  return useMutation<BackupCodesResponse, ApiError, void>({
    mutationFn: regenerateBackupCodes,
  })
}

export function useLogout() {
  const qc = useQueryClient()
  return () => {
    clearTokens()
    qc.removeQueries({ queryKey: ME_QUERY_KEY })
    qc.removeQueries({ queryKey: ME_EXTENDED_QUERY_KEY })
    qc.removeQueries({ queryKey: ME_SESSIONS_QUERY_KEY })
  }
}
